/* global ZOHO */

import React, { useState, useEffect } from 'react';
import { getModuleFields, fetchLayoutsFromAPI, fetchFormsFromAPI, fetchPipelinesFromAPI } from '../services/apiService';
import '../index.css'; // Corrected import path for the CSS file
import logger from '../utils/logger'; // Import the logger

const MEDICARE_FORMS = [
  'Medicare Packet',
  'Medicare Intake',
  'Scope of Appointment',
  'Medicare Intake Agent',
  'Medicare Intake - Agent',
  'Medicare Drug Review',
  'MAPD Review'
];

const INDIVIDUAL_FAMILY_FORMS = [
  'Individual-Family Intake',
  'Individual-Family Consent',
  'Individual-Family - Agent',
  'Individual-Family Packet',
  'Individual-Family Review',
  'Individual-Family Consent - Oregon',
  'Individual-Family Packet - Oregon'
];

function OppForm({ EntityId }) {
  const [formData, setFormData] = useState({
    Deal_Name: '',
    Layout: '',
    Pipeline: '',
    Stage: '',
    Select_Form: '',
    Date_Field: '',
    Type: '' // Add Type to formData
  });

  const [layoutOptions, setLayoutOptions] = useState([]);
  const [pipelineOptions, setPipelineOptions] = useState([]);
  const [currentStages, setCurrentStages] = useState([]);
  const [formOptions, setFormOptions] = useState([]); // Initialize as an empty array
  const [typeOptions, setTypeOptions] = useState([]); // Initialize as an empty array
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recordInfo, setRecordInfo] = useState(null); // State to store fetched record data
  const [ownerName, setOwnerName] = useState(''); // State to store owner's name
  const [errors, setErrors] = useState({}); // State to store form validation errors
  const [errorMessage, setErrorMessage] = useState(''); // State to store error messages
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const selectedLayout = layoutOptions.find(layout => layout.id === formData.Layout);
  const selectedLayoutName = selectedLayout?.name || '';
  const filteredFormOptions = formOptions.filter(form => {
    if (selectedLayoutName === 'Medicare') {
      return MEDICARE_FORMS.includes(form.name);
    }
    if (selectedLayoutName === 'Individual/Family') {
      return INDIVIDUAL_FAMILY_FORMS.includes(form.name);
    }
    return true;
  });

  const initializeData = async () => {
    try {
      logger.debug("Initializing data with EntityId:", EntityId);
      await fetchLayouts();
      await fetchForms();
      const typePicklistValues = await getModuleFields("Deals"); // Fetch and set Type options
      setTypeOptions(typePicklistValues);
      const recordData = await fetchRecordData(EntityId); // Fetch record data
      setRecordInfo(recordData);
      if (recordData && recordData.Owner) {
        setOwnerName(recordData.Owner.name); // Store owner's name for future use
      }
    } catch (error) {
      logger.error("Error initializing data:", error);
      setErrorMessage("Failed to initialize data. Please try again.");
    }
  };

  const fetchLayouts = async () => {
    try {
      logger.debug("Fetching layouts...");
      const layouts = await fetchLayoutsFromAPI();
      logger.debug("Layouts fetched:", layouts);
      setLayoutOptions(layouts);
    } catch (error) {
      logger.error("Error fetching layouts:", error);
      setErrorMessage("Failed to fetch layouts. Please try again.");
    }
  };

  const fetchForms = async () => {
    try {
      logger.debug("Fetching forms...");
      const formsResponse = await fetchFormsFromAPI();
      logger.debug("Forms fetched:", formsResponse);

      if (formsResponse) {
        setFormOptions(formsResponse);
      } else {
        logger.error("Forms response data is undefined");
        setErrorMessage("Failed to fetch forms. Please try again.");
      }
    } catch (error) {
      logger.error("Error fetching forms:", error);
      setErrorMessage("Failed to fetch forms. Please try again.");
    }
  };

  const fetchPipelines = async (layoutId) => {
    try {
      logger.debug("Fetching pipelines for layout:", layoutId);
      const pipelines = await fetchPipelinesFromAPI(layoutId);
      logger.debug("Pipelines fetched:", pipelines);
      setPipelineOptions(pipelines);
    
      // Populate stages for the first pipeline
      if (pipelines.length > 0 && pipelines[0].stages) {
        setCurrentStages(pipelines[0].stages);
      } else {
        setCurrentStages([]);
      }
    } catch (error) {
      logger.error("Error fetching pipelines:", error);
      setErrorMessage("Failed to fetch pipelines. Please try again.");
    }
  };

  const fetchRecordData = async (recordId) => {
    try {
      logger.debug("Fetching record data for RecordID:", recordId);
      const response = await ZOHO.CRM.API.getRecord({
        Entity: "Contacts",
        RecordID: recordId
      });
      logger.debug("Record data fetched:", response);
      return response.data[0]; // Assuming the data is in the first element of the array
    } catch (error) {
      logger.error("Error fetching record data:", error);
      setErrorMessage("Failed to fetch record data. Please try again.");
      return null;
    }
  };

  useEffect(() => {
    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [EntityId]);

  useEffect(() => {
    if (formData.Layout) {
      fetchPipelines(formData.Layout);
    }
  }, [formData.Layout]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.Deal_Name) newErrors.Deal_Name = "Opportunity Name is required";
    if (!formData.Layout) newErrors.Layout = "Layout is required";
    if (!formData.Pipeline) newErrors.Pipeline = "Pipeline is required";
    if (!formData.Stage) newErrors.Stage = "Stage is required";
    if (!formData.Select_Form) newErrors.Select_Form = "Form selection is required";
    if (!formData.Date_Field) newErrors.Date_Field = "Date is required";
    if (!formData.Type) newErrors.Type = "Type is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      logger.warn("Form validation failed");
      return;
    }

    logger.debug("handleSubmit called");

    setIsSubmitting(true);
    setIsLoading(true);
    console.log("Loading started, isSubmitting and isLoading set to true");

    try {
      logger.debug("Submitting form data:", formData);

      const recordData = {
        Deal_Name: formData.Deal_Name,
        Layout: formData.Layout,
        Pipeline: formData.Pipeline,
        Stage: formData.Stage,
        Closing_Date: formData.Date_Field,
        Form: formData.Select_Form,
        Contact_Name: {
          id: EntityId // Ensure this is a single value, not an array
        },
        Type: formData.Type, // Add Type to recordData
        Owner: {
          id: recordInfo?.Owner?.id // Add Owner id to recordData
        }
      };

      logger.debug("Fetched record info:", recordData);

      logger.debug("Attempting to insert record");
      const insertResponse = await window.ZOHO.CRM.API.insertRecord({
        Entity: "Deals",
        APIData: recordData,
        Trigger: ["workflow"]
      });
      logger.debug("Insert record response:", insertResponse);

      // Add 2-second delay here
      await new Promise(resolve => setTimeout(resolve, 2000));

      if (insertResponse.data && insertResponse.data.length > 0 && insertResponse.data[0].code === "SUCCESS") {
        logger.info("Record created successfully!");

        const newRecordId = insertResponse.data[0].details.id;

        // Execute custom function
        const req_data = {
          "arguments": JSON.stringify({
            "opp_owner_name": ownerName,
            "deal_id": newRecordId,
            "contact_id": EntityId
          })
        };

        logger.debug("Executing custom function with data:", req_data);

        const customFunctionResponse = await ZOHO.CRM.FUNCTIONS.execute("send_form_dynamically_api", req_data);
        logger.debug("Custom function executed:", customFunctionResponse);
        
        setSuccessMessage("Form was sent successfully!");
        console.log("Success message set");
      } else {
        const errorData = insertResponse?.data?.[0];
        const isOwnerError =
          errorData?.details?.api_name === 'Owner' ||
          JSON.stringify(errorData).toLowerCase().includes('owner');
        if (isOwnerError) {
          throw new Error("The contact's owner is inactive. Please reassign the contact to an active user before submitting.");
        }
        throw new Error("Failed to create record. Please try again.");
      }
    } catch (error) {
      logger.error("Error submitting form:", error);
      setErrorMessage(error.message || "Failed to submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
      console.log("Loading stopped, isSubmitting and isLoading set to false");
    }
  };

  return (
    <div className="opp-form-container">
      {(isSubmitting || isLoading) && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <div>Submitting...</div>
          </div>
        </div>
      )}
      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="error-message">
          {errorMessage}
        </div>
      )}
      <form onSubmit={handleSubmit} className="opp-form">
        {/* Opportunity Name Field */}
        <div className="form-group">
          <label htmlFor="deal_name">Opportunity Name</label>
          <input
            type="text"
            id="deal_name"
            value={formData.Deal_Name}
            onChange={(e) => setFormData({ ...formData, Deal_Name: e.target.value })}
          />
          {errors.Deal_Name && <span className="error">{errors.Deal_Name}</span>}
        </div>
        {/* Type Field */}
        <div className="form-group">
          <label htmlFor="type">Type</label>
          <select
            id="type"
            value={formData.Type}
            onChange={(e) => setFormData({ ...formData, Type: e.target.value })}
          >
            <option value="">Select Type</option>
            {(Array.isArray(typeOptions) ? typeOptions : []).map((type, index) => (
              <option key={index} value={type}>
                {type}
              </option>
            ))}
          </select>
          {errors.Type && <span className="error">{errors.Type}</span>}
        </div>
        {/* Layout Field */}
        <div className="form-group">
          <label htmlFor="layout">Layout</label>
          <select
            id="layout"
            value={formData.Layout}
            onChange={(e) => {
              setFormData({ ...formData, Layout: e.target.value, Select_Form: '' });
              fetchPipelines(e.target.value);
            }}
          >
            <option value="">Select Layout</option>
            {layoutOptions.map((layout, index) => (
              <option key={index} value={layout.id}>
                {layout.name}
              </option>
            ))}
          </select>
          {errors.Layout && <span className="error">{errors.Layout}</span>}
        </div>
        {/* Pipeline Field */}
        <div className="form-group">
          <label htmlFor="pipeline">Pipeline</label>
          <select
            id="pipeline"
            value={formData.Pipeline}
            onChange={(e) => {
              const selectedPipelineId = e.target.value;
              setFormData({ ...formData, Pipeline: selectedPipelineId, Stage: '' });
              const selectedPipeline = pipelineOptions.find(p => p.id === selectedPipelineId);
              if (selectedPipeline && selectedPipeline.stages) {
                setCurrentStages(selectedPipeline.stages);
              } else {
                setCurrentStages([]);
              }
            }}
          >
            <option value="">Select Pipeline</option>
            {pipelineOptions.map((pipeline, index) => (
              <option key={index} value={pipeline.id}>
                {pipeline.name}
              </option>
            ))}
          </select>
          {errors.Pipeline && <span className="error">{errors.Pipeline}</span>}
        </div>
        {/* Stage Field */}
        <div className="form-group">
          <label htmlFor="stage">Stage</label>
          <select
            id="stage"
            value={formData.Stage}
            onChange={(e) => setFormData({ ...formData, Stage: e.target.value })}
          >
            <option value="">Select Stage</option>
            {currentStages.map((stage, index) => (
              <option key={index} value={stage.id}>
                {stage.name}
              </option>
            ))}
          </select>
          {errors.Stage && <span className="error">{errors.Stage}</span>}
        </div>
        {/* Select Form Field */}
        <div className="form-group">
          <label htmlFor="select_form">Select Form</label>
          <select
            id="select_form"
            value={formData.Select_Form}
            onChange={(e) => setFormData({ ...formData, Select_Form: e.target.value })}
          >
            <option value="">Select Form</option>
            {filteredFormOptions.map((form, index) => (
              <option key={index} value={form.name}>
                {form.name}
              </option>
            ))}
          </select>
          {errors.Select_Form && <span className="error">{errors.Select_Form}</span>}
        </div>
        {/* Date Field */}
        <div className="form-group">
          <label htmlFor="date_field">Date Field</label>
          <input
            type="date"
            id="date_field"
            value={formData.Date_Field}
            onChange={(e) => setFormData({ ...formData, Date_Field: e.target.value })}
          />
          {errors.Date_Field && <span className="error">{errors.Date_Field}</span>}
        </div>
        <button type="submit" className="submit-button" disabled={isSubmitting || isLoading}>
          {isSubmitting || isLoading ? 'Submitting...' : 'Submit'}
        </button>
      </form>
    </div>
  );
}

export default OppForm;
